import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';
import { AgencyRequest } from '../../administration/interfaces/add-to-worklist';
import { VwCompleteWorklist } from '../../administration/interfaces/complete-worklist';
import { VwLcgmsSeitLocation } from '../../administration/interfaces/lcgms-seit-location';
import { VwMdeWaitlist } from '../../administration/interfaces/mde-waitlist';
import { WorklistSearch } from '../../administration/interfaces/worklist-search';
import { AgencyList, VwAgency } from '../interface/agency-list';
import { WaitlistRequest } from '../models/waitlist-request';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public getGobalWorklist(): Observable<VwCompleteWorklist[]> {
    return this.http
      .get<VwCompleteWorklist[]>(`${this.config.apiUrl}/MDE/Worklist`)
      .pipe(catchError(handleError));
  }

  public getMdeWaitlist(agencySk: string): Observable<VwMdeWaitlist[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/Waitlist/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<VwMdeWaitlist[]>(url)
      .pipe(catchError(handleError));
  }

  // TODO: There is no endpoint in the backend that takes in the `agencySk` from the route, only the request.
  public addToMDEWaitlist(
    agencySk: string,
    request: AgencyRequest[]
  ): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/AddToWorklist/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  // TODO: This method needs to be updated to only pass in the parameter of `AgencyRequest` as this is the only parameter the backend accepts.
  // I believe the `studentSk` and `agencySk` are not needed.
  public addToWaitlist(
    studentSk: string,
    agencySk: string,
    request: AgencyRequest
  ): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/AddToWaitlist/${encodeURIComponent(studentSk)}/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public getMdeAgency(): Observable<AgencyList[]> {
    return this.http
      .get<AgencyList[]>(`${this.config.apiUrl}/MDE/MDEAgencies`)
      .pipe(catchError(handleError));
  }

  public saveMdeAgency(request: AgencyList): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/Agency`, request)
      .pipe(catchError(handleError));
  }

  public getAgencyDetails(studentSk: string): Observable<VwAgency[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/GetMDEListByStudentSk/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<VwAgency[]>(url)
      .pipe(catchError(handleError));
  }

  public getMdeUserAgencies(): Observable<AgencyList[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/UserAgencies`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyList[]>(url)
      .pipe(
        map((agencies: AgencyList[]) =>
          agencies.filter(
            (agency, index, self) =>
              index === self.findIndex((x) => x.agencySk === agency.agencySk)
          )
        ),
        catchError(handleError)
      );
  }

  public getMdeAgencyByAgencySk(agencySk: string): Observable<AgencyList> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/MDEAgency/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyList>(url)
      .pipe(catchError(handleError));
  }

  // TODO: I dont think the `agencySk` is needed here, as the backend only accepts the `request` parameter.
  public removeFromWaitlist(
    agencySk: string,
    request: AgencyList[]
  ): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/RemoveFromWaitlist/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public addToMDWaitlist(request: WaitlistRequest): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.config.apiUrl}/MDE/AddToMDEWaitlist`, request)
      .pipe(catchError(handleError));
  }

  public getStudentSearchDetails(
    request: WorklistSearch
  ): Observable<VwCompleteWorklist[]> {
    return this.http
      .post<VwCompleteWorklist[]>(`${this.config.apiUrl}/MDE/Worklist`, request)
      .pipe(catchError(handleError));
  }

  public searchAgencyList(
    agencyName: string,
    locationCode: string
  ): Observable<VwLcgmsSeitLocation[]> {
    const params: HttpParams = new HttpParams()
      .set('agencyName', agencyName)
      .set('loctionCode', locationCode);
    return this.http
      .get<VwLcgmsSeitLocation[]>(
        `${this.config.apiUrl}/MDE/SearchSEITLocations`,
        { params }
      )
      .pipe(catchError(handleError));
  }

  // TODO: was not able to find this endpoint in the backend
  public getEvaluatorProvidersByAgency(agencySk: string): Observable<unknown> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/AgencyManagement/EvaluatorProvidersByAgency/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get(url)
      .pipe(catchError(handleError));
  }
  public getMedicaidParentalConsentDetails(pageNumber: number, pageSize: number): Observable<unknown> {
    const params = new HttpParams()
    .set('pageNumber', pageNumber)
    .set('pageSize', pageSize);

    return this.http
      .get(`${this.config.apiUrl}/Dashboard/GetMedicaidParentalForm/`, { params })
      .pipe(catchError(handleError));
  }
  public addParentMedicalRecords(request: unknown): Observable<unknown> {
    return this.http
      .post(
        `${this.config.apiUrl}/CommonConsent/AddParentMedicalRecords/`,
        request
      )
      .pipe(catchError(handleError));
  }
}
